import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import EducationForm from '../forms/EducationForm';
import useEducations from 'features/feature_user/hooks/useEducations';
import useProfile from 'features/feature_user/hooks/UseProfile';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import EducationList from './EducationList';
import { useQueryClient } from 'react-query';
const EducationSection = ({ cv, compact = false, onAddClick, triggerAdd, readonly = false }) => {
    // useEducations hook now uses React Query for data fetching and mutations
    const { educations, addEducation, updateEducation, deleteEducation, isLoading } = useEducations(cv?.id);
    const { refreshProfile } = useProfile();
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEducation, setSelectedEducation] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const prevTriggerRef = useRef(0);

    const queryClient = useQueryClient();

    const handleAdd = () => {
        setSelectedEducation(null);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (triggerAdd && triggerAdd !== prevTriggerRef.current) {
            prevTriggerRef.current = triggerAdd;
            handleAdd();
        }
    }, [triggerAdd]);

    const handleEdit = (education) => {
        setSelectedEducation(education);
        setIsModalOpen(true);
    };

    const handleDelete = (education) => {
        setSelectedEducation(education);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        await queryClient.invalidateQueries(['educations', cv?.id || null]);
        await queryClient.refetchQueries(['educations', cv?.id || null]);
        await queryClient.invalidateQueries(['educations']);
        setIsDeleteModalOpen(false);
    };

    const content = (
        <>
            {error && (
                <Typography color="error" className="mb-4">
                    {error}
                </Typography>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <Typography>Loading education...</Typography>
                </Box>
            ) : (
                <EducationList 
                    educations={educations} 
                    onEdit={readonly ? undefined : handleEdit} 
                    onDelete={readonly ? undefined : handleDelete}
                    compact={compact}
                />
            )}

            <CustomModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                title={selectedEducation ? 'Edit Education' : 'Add New Education'}
            >
                <EducationForm 
                    id={selectedEducation?.id}
                    onCancel={() => setIsModalOpen(false)}
                    cvId={cv?.id}
                />
            </CustomModal>

            <DeleteDialog
                url={`/job/cv/education/${selectedEducation?.id}`}
                isOpen={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                notifyDone={handleConfirmDelete}
                message="Are you sure you want to delete this education entry?"
            />
        </>
    );

    if (compact) {
        return content;
    }

    return (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Education
                </Typography>
                {!readonly && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                )}
            </Box>
            {content}
        </Paper>
    );
};

export default EducationSection; 